import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import { CreatePaymentResponse } from "../../membership/CreatePaymentResponse";
import { usePaymentClient } from "../PaymentClient";
import { useCustomerMembershipContext } from "./CustomerMembershipContext";
import { usePaymentUpdates } from "./PaymentStatus";
import { PaymentViewModel } from "../PaymentViewModel";
import { getRelativeUrlPart } from "../../utils";

export function useMembershipPaymentClient() {
  const history = useHistory();
  const { customer, yearlyProducts: products } = useCustomerMembershipContext();
  const paymentClient = usePaymentClient<CreatePaymentResponse>();
  const paymentId = paymentClient.data
    ? paymentClient.data.payment.paymentDetails.id
    : null;

  const paymentParameters = useMemo(
    () => (paymentId ? { id: paymentId, customerGuid: customer.id } : null),
    [paymentId, customer]
  );

  const { payment: updatedPayment, isSubscribed: isWaitingForUrl } =
    usePaymentUpdates(paymentParameters);

  const [payment, setPayment] = useState<PaymentViewModel | null>(null);

  useEffect(
    () => setPayment(paymentClient.data?.payment || null),
    [paymentClient.data]
  );
  useEffect(() => {
    if (updatedPayment) setPayment(updatedPayment);
  }, [updatedPayment]);

  async function initiateCheckout(productId: string) {
    const product = products.filter((p) => p.id === productId)[0];
    const paymentFields = {
      productId: product.id,
      payment: {
        amount: product.price,
        taxAmount: product.tax,
      },
    };
    const payload = { ...paymentFields };

    const result = await paymentClient.fetch({
      method: "POST",
      path: `membership/customer/${customer.id}/payment`,
      body: payload,
    });

    if (result) {
      const { url, payment } = result;
      history.push(getRelativeUrlPart(new URL(url)));
      const { paymentUrl } = payment.paymentDetails;
      if (paymentUrl) window.location.href = paymentUrl;
    }
  }

  return {
    initiateCheckout,
    isLoading: isWaitingForUrl || paymentClient.loading,
    isWaitingForUrl,
    isCreating: paymentClient.loading,
    error: paymentClient.error,
    payment,
  };
}
